import React from 'react';
import { CheckCircle } from 'lucide-react';
import { sectionTitles, defaultPlaceholderData } from '../common/resumeConstants';
import { 
  ContactInfo,
  getPlaceholderClass,
  ExperienceSection,
  EducationSection,
  CertificateSection,
  SkillSection,
  PaperSection,
  CoreCompetenciesSection,
  SummarySection
} from '../common/ResumeComponents';
import { 
  checkHasRealData, 
  getCurrentData, 
  getPaperTypeText
} from '../common/resumeHelpers';

// Template 3 - 모던한 회계사 스타일
const Template3 = ({ resumeData, updatingField, language = 'kr' }) => {
  const titles = sectionTitles[language] || sectionTitles.kr;
  const hasRealData = checkHasRealData(resumeData);
  const currentData = getCurrentData(resumeData, defaultPlaceholderData, hasRealData);

  // 이름을 성과 이름으로 분리
  const nameParts = currentData.name.split(' ');
  const firstName = nameParts[0] || currentData.name;
  const lastName = nameParts.slice(1).join(' ') || '';

  return (
    <div className={`template-3 resume-container bg-white ${language === 'en' ? 'resume-english' : ''}`}>
      {/* Header */}
      <div className="px-6 pt-6 pb-3">
        <div className="flex justify-between items-start">
          <div>
            <h1 className={`text-3xl font-bold text-gray-900 mb-3 ${!hasRealData.name ? 'italic text-gray-300' : ''}`}>
              {firstName}
            </h1>
            {lastName && (
              <h1 className={`text-3xl font-bold text-gray-900 mb-3 ${!hasRealData.name ? 'italic text-gray-300' : ''}`}>
                {lastName}
              </h1>
            )}
            <p className={!hasRealData.title ? 'text-gray-300 italic text-lg' : 'text-gray-500 text-lg'}>
              {currentData.title}
            </p>
          </div>
          
          {/* Contact Info - Right Side */}
          <ContactInfo
            phone={currentData.phone}
            email={currentData.email}
            address={currentData.address}
            socialLinks={currentData.socialLinks}
            hasRealData={hasRealData}
            direction="vertical"
            align="right"
            useEmoji={false}
            className="mt-2"
          />
        </div>
      </div>

      <main className="p-6 space-y-6">
      {/* Introduction & Key Strengths */}
      {(currentData.summary || currentData.keyStrengths.length > 0) && (
        <section>
          <div className="bg-gray-100 px-6 py-2 -mx-6 mb-4">
            <h2 className="text-xl font-bold text-gray-800 uppercase">{titles.introduction}</h2>
          </div>
          <div className="space-y-4">
            <SummarySection
              summary={currentData.summary}
              hasRealData={hasRealData}
              textClassName="text-sm text-gray-700 mb-4"
            />
            {currentData.keyStrengths.length > 0 && (
              <div>
                <h3 className="text-base font-semibold text-gray-700 mb-3">{titles.coreCompetencies}</h3>
                <CoreCompetenciesSection
                  keyStrengths={currentData.keyStrengths}
                  hasRealData={hasRealData}
                  className="space-y-1"
                  itemClassName=""
                  bulletStyle={<CheckCircle className="w-4 h-4 flex-shrink-0 mt-0.5 text-gray-400" />}
                  textClassName="text-sm text-gray-700"
                />
              </div>
            )}
          </div>
        </section>
      )}

      {/* Work Experience */}
      {currentData.experiences.length > 0 && (
        <section>
          <div className="bg-gray-100 px-6 py-2 -mx-6 mb-4">
            <h2 className="text-xl font-bold text-gray-800 uppercase">{titles.experience}</h2>
          </div>
          <ExperienceSection
            experiences={currentData.experiences}
            hasRealData={hasRealData}
            className="space-y-6"
            itemClassName=""
            datePosition="right"
            showProjects={true}
            projectIndent="pl-4 border-l-2 border-gray-200"
            bulletStyle="•"
            dateClassName="text-sm text-gray-600"
            companyClassName="font-medium text-gray-700 text-sm"
            positionClassName="text-lg font-semibold text-gray-800"
            descriptionClassName="text-sm text-gray-700"
            projectTitleClassName="font-semibold text-gray-700 text-sm mb-2"
            projectDescriptionClassName="text-sm text-gray-600"
          />
        </section>
      )}

      {/* Education */}
      {currentData.education.length > 0 && (
        <section>
          <div className="bg-gray-100 px-6 py-2 -mx-6 mb-4">
            <h2 className="text-xl font-bold text-gray-800 uppercase">{titles.education}</h2>
          </div>
          <EducationSection
            education={currentData.education}
            hasRealData={hasRealData}
            className=""
            itemClassName="flex flex-col sm:flex-row justify-between sm:items-start mb-3 pb-3 border-b border-dotted border-gray-200 last:border-b-0 last:pb-0 last:mb-0"
            datePosition="right"
            showMajor={true}
            dateClassName="text-xs sm:text-sm mt-1 sm:mt-0 whitespace-nowrap text-gray-600"
            schoolClassName="text-sm text-gray-700"
            degreeClassName="text-base font-semibold text-gray-800"
            majorClassName="text-xs text-gray-400"
          />
        </section>
      )}

      {/* Papers */}
      {hasRealData.papers && (
        <section>
          <div className="bg-gray-100 px-6 py-2 -mx-6 mb-4">
            <h2 className="text-xl font-bold text-gray-800 uppercase">{titles.papers}</h2>
          </div>
          <PaperSection
            papers={currentData.papers}
            hasRealData={hasRealData}
            getPaperTypeText={getPaperTypeText}
            className="space-y-4"
            itemClassName=""
            titleClassName="text-base font-semibold text-gray-800"
            authorsClassName="text-sm text-gray-600"
            journalClassName="text-sm text-gray-500"
            typeClassName="text-xs bg-gray-200 text-gray-700 px-2 py-1 rounded"
            showType={true}
          />
        </section>
      )}

      {/* Certificates */}
      {currentData.certificates.length > 0 && (
        <section>
          <div className="bg-gray-100 px-6 py-2 -mx-6 mb-4">
            <h2 className="text-xl font-bold text-gray-800 uppercase">{titles.certificates}</h2>
          </div>
          <div className="space-y-3">
            {currentData.certificates.map((cert, index) => (
              <div key={index} className="flex justify-between items-baseline">
                <div>
                  <h3 className={getPlaceholderClass(hasRealData.certificates, "text-sm font-semibold text-gray-800")}>
                    {cert.name}
                  </h3>
                  <p className={getPlaceholderClass(hasRealData.certificates, "text-xs text-gray-600")}>
                    {cert.issuer}
                  </p>
                </div>
                <span className={getPlaceholderClass(hasRealData.certificates, "text-xs text-gray-500")}>
                  {cert.date}
                </span>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Skills */}
      {currentData.skills.length > 0 && (
        <section>
          <div className="bg-gray-100 px-6 py-2 -mx-6 mb-4">
            <h2 className="text-xl font-bold text-gray-800 uppercase">{titles.skills}</h2>
          </div>
          <SkillSection
            skills={currentData.skills}
            hasRealData={hasRealData}
            variant="tags"
            className="flex flex-wrap gap-2"
            tagClassName="px-3 py-1 text-sm rounded-full font-medium transition-all duration-300 bg-gray-100 text-gray-700 border border-gray-300 hover:bg-gray-200"
          />
        </section>
      )}
      </main>
    </div>
  );
};

export default Template3;
